import { AlertTriangle, FileSpreadsheet, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import {
  IMPORT_STATUS_LABEL,
  MATERIAL_IMPORT_COLUMNS,
  type ColumnMapping,
  type ImportRowStatus,
  type MaterialImportField,
} from '@obraplan/core/domain/schedule/material-import';
import type { ImportPreview } from '@obraplan/core/application/use-cases/schedule/material-import-use-cases';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableWrapper,
} from '@/components/ui/table';

const STATUS_TONE: Readonly<
  Record<ImportRowStatus, 'neutral' | 'ok' | 'info' | 'warn' | 'danger'>
> = {
  CREATE: 'ok',
  UPDATE: 'info',
  UNCHANGED: 'neutral',
  INVALID: 'danger',
  DUPLICATE: 'warn',
};

export interface MaterialImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preview: ImportPreview | undefined;
  /** Lê o arquivo e devolve a previsão; `mapping` reprocessa o mesmo arquivo. */
  onFile: (file: File) => void;
  onRemap: (mapping: ColumnMapping, fallbackUnitId: string | undefined) => void;
  onConfirm: (updateExisting: boolean) => void;
  isPending: boolean;
  errorMessage: string | undefined;
}

/**
 * Importar o catálogo do sistema de compras.
 *
 * Três momentos na mesma tela: escolher o arquivo, conferir o que foi entendido de cada
 * coluna e ver linha a linha o que vai acontecer. Nada é gravado até a confirmação —
 * quem importa vê antes quantos itens são novos, quantos seriam alterados e quais linhas
 * têm problema.
 *
 * Aceita `.csv` e `.xlsx`; o formato é decidido pelo conteúdo, não pela extensão.
 */
export function MaterialImportDialog({
  open,
  onOpenChange,
  preview,
  onFile,
  onRemap,
  onConfirm,
  isPending,
  errorMessage,
}: MaterialImportDialogProps) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [updateExisting, setUpdateExisting] = useState(true);

  const mapping = preview?.mapping ?? {};
  const missingRequired = MATERIAL_IMPORT_COLUMNS.filter(
    (column) => column.required && mapping[column.field] === undefined,
  );

  const changeMapping = (field: MaterialImportField, header: string): void => {
    const next: ColumnMapping = { ...mapping };
    if (header === '') delete next[field];
    else next[field] = header;
    onRemap(next, fallbackUnitId);
  };

  const [fallbackUnitId, setFallbackUnitId] = useState<string | undefined>(undefined);

  const changeFallback = (unitId: string): void => {
    const value = unitId === '' ? undefined : unitId;
    setFallbackUnitId(value);
    onRemap(mapping, value);
  };

  const applicable =
    preview === undefined
      ? 0
      : preview.summary.create + (updateExisting ? preview.summary.update : 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Importar catálogo de materiais</DialogTitle>
          <DialogDescription>
            Exporte o cadastro de itens do seu sistema de compras em CSV ou XLSX. O código de lá
            vira o código aqui — é ele que liga os dois sistemas.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {/* ── 1. Arquivo ───────────────────────────────────────────────── */}
          <div className="flex flex-wrap items-center gap-3 rounded-md border border-(--color-border-subtle) bg-(--color-surface-muted) p-3">
            <FileSpreadsheet className="size-5 text-(--color-content-muted)" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              {preview === undefined ? (
                <p className="text-sm text-(--color-content-muted)">
                  Nenhum arquivo escolhido. A primeira linha deve ter os títulos das colunas.
                </p>
              ) : (
                <p className="truncate text-sm">
                  <strong>{preview.fileName}</strong>{' '}
                  <span className="text-(--color-content-muted)">
                    · {preview.format}
                    {preview.delimiter === undefined
                      ? ''
                      : ` · separador "${preview.delimiter === '\t' ? 'tab' : preview.delimiter}"`}
                    {preview.sheetName === undefined ? '' : ` · ${preview.sheetName}`} ·{' '}
                    {preview.summary.total} linhas
                  </span>
                </p>
              )}
            </div>
            <input
              ref={fileInput}
              type="file"
              accept=".csv,.tsv,.txt,.xlsx"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file !== undefined) onFile(file);
                event.target.value = '';
              }}
            />
            <Button type="button" variant="secondary" onClick={() => fileInput.current?.click()}>
              <Upload aria-hidden="true" />
              {preview === undefined ? 'Escolher arquivo' : 'Trocar arquivo'}
            </Button>
          </div>

          {preview !== undefined && (
            <>
              {/* ── 2. Colunas ─────────────────────────────────────────────── */}
              <div>
                <h3 className="mb-2 text-sm font-medium">Colunas do arquivo</h3>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {MATERIAL_IMPORT_COLUMNS.map((column) => (
                    <Field
                      key={column.field}
                      label={column.label}
                      htmlFor={`map-${column.field}`}
                      required={column.required}
                    >
                      <select
                        id={`map-${column.field}`}
                        className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm"
                        value={mapping[column.field] ?? ''}
                        onChange={(event) => changeMapping(column.field, event.target.value)}
                      >
                        <option value="">— não importar —</option>
                        {preview.headers.map((header) => (
                          <option key={header} value={header}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </Field>
                  ))}

                  <Field
                    label="Unidade padrão"
                    htmlFor="map-fallback-unit"
                    hint="Usada quando a linha não traz unidade ou ela não é reconhecida."
                  >
                    <select
                      id="map-fallback-unit"
                      className="h-11 w-full rounded-md border border-(--color-border-subtle) bg-(--color-surface) md:h-9 px-3 text-sm"
                      value={fallbackUnitId ?? ''}
                      onChange={(event) => changeFallback(event.target.value)}
                    >
                      <option value="">— recusar a linha —</option>
                      {preview.units.map((unit) => (
                        <option key={unit.id} value={unit.id}>
                          {unit.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                </div>

                {missingRequired.length > 0 && (
                  <p
                    role="alert"
                    className="mt-2 flex items-center gap-2 text-sm text-(--color-state-danger)"
                  >
                    <AlertTriangle className="size-4" aria-hidden="true" />
                    Indique qual coluna traz{' '}
                    {missingRequired.map((column) => column.label.toLowerCase()).join(' e ')}.
                  </p>
                )}
              </div>

              {/* ── 3. O que vai acontecer ─────────────────────────────────── */}
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="ok">{preview.summary.create} novos</Badge>
                <Badge tone="info">{preview.summary.update} atualizam</Badge>
                {preview.summary.unchanged > 0 && (
                  <Badge tone="neutral">{preview.summary.unchanged} sem mudança</Badge>
                )}
                {preview.summary.duplicate > 0 && (
                  <Badge tone="warn">{preview.summary.duplicate} repetidos no arquivo</Badge>
                )}
                {preview.summary.invalid > 0 && (
                  <Badge tone="danger">{preview.summary.invalid} com erro</Badge>
                )}
              </div>

              <TableWrapper className="max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16">Linha</TableHead>
                      <TableHead>Situação</TableHead>
                      <TableHead>Código</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Unidade</TableHead>
                      <TableHead>Observação</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.rows.slice(0, 200).map((row) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell className="tabular text-(--color-content-muted)">
                          {row.rowNumber}
                        </TableCell>
                        <TableCell>
                          <Badge tone={STATUS_TONE[row.status]}>
                            {IMPORT_STATUS_LABEL[row.status]}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-xs">{row.code || '—'}</TableCell>
                        <TableCell className="max-w-64 truncate">{row.name || '—'}</TableCell>
                        <TableCell>{row.unitLabel}</TableCell>
                        <TableCell className="text-xs text-(--color-content-muted)">
                          {row.issues.map((issue) => issue.message).join(' ')}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableWrapper>

              {preview.rows.length > 200 && (
                <p className="text-xs text-(--color-content-muted)">
                  Mostrando as 200 primeiras linhas; a importação considera todas as{' '}
                  {preview.summary.total}.
                </p>
              )}

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={updateExisting}
                  onChange={(event) => setUpdateExisting(event.target.checked)}
                />
                Atualizar os materiais que já existem com o mesmo código
              </label>
            </>
          )}

          {errorMessage !== undefined && (
            <p role="alert" className="text-sm text-(--color-state-danger)">
              {errorMessage}
            </p>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            type="button"
            variant="primary"
            disabled={
              isPending || preview === undefined || applicable === 0 || missingRequired.length > 0
            }
            onClick={() => onConfirm(updateExisting)}
          >
            {isPending ? 'Importando…' : `Importar ${String(applicable)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
